/**
 * Shared plan + add-on checkout via Razorpay (free plans skip payment).
 * Used by CreditsManagement (/credits) for plan subscribe, upgrade, and token-pack top-up.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, CircularProgress, Alert, Dialog,
  DialogContent, TextField, IconButton, Grid, Autocomplete,
} from '@mui/material';
import {
  CheckCircle, Payment, Autorenew, Close,
  LocationOn, Shield, LocalOffer, Stars, CalendarToday, AddCircle, ErrorOutline, CancelOutlined,
} from '@mui/icons-material';
import {
  checkoutPlan, checkoutTopup, createCheckoutOrder, createTopupOrder,
  validateMerchantCoupon, getCheckoutAddressPrefill,
} from '../../../services/subscriptionAPI';
import { locationAPI } from '../../../services/merchantAPI';
import { useRazorpay, formatRazorpayContact } from '../../../hooks/useRazorpay';
import FlutterServicesAPI from '../../../services/flutterServices';

const PURPLE      = '#6318C6';
const PURPLE_DARK = '#5210a8';
const PURPLE_SOFT = '#f3eeff';
const PURPLE_MID  = '#ede9fe';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: '#fff',
    fontSize: 14,
    transition: 'all 0.2s ease',
    '& fieldset': {
      borderColor: '#cbd5e1',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#c084fc' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: PURPLE,
      borderWidth: 2,
      boxShadow: `0 0 0 4px rgba(99, 24, 198, 0.15)`,
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: 14,
    color: '#64748b',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: PURPLE,
    fontWeight: 600,
  },
  '& .MuiFormHelperText-root': {
    fontSize: 12,
    mt: 0.5,
  },
};

// ─── GST helper ─────────────────────────────────────────────────────────────

function computeGSTFromAddress(billingAddress, pricing) {
  const country = (billingAddress?.country_name || '').trim().toLowerCase();
  const state   = (billingAddress?.state_name   || '').trim().toLowerCase();
  const exclTax = parseFloat(pricing?.mrp_excl_tax ?? pricing?.price ?? 0);
  const inclTax = parseFloat(pricing?.mrp_incl_tax ?? 0);

  if (country && country !== 'india') {
    return { taxType: 'none', cgst: 0, sgst: 0, igst: 0, total: 0, base: exclTax, grand: exclTax };
  }
  const taxTotal = inclTax > 0 && exclTax > 0
    ? parseFloat((inclTax - exclTax).toFixed(2))
    : parseFloat((exclTax * 0.18).toFixed(2));
  const grand = inclTax || parseFloat((exclTax + taxTotal).toFixed(2));

  if (state === 'maharashtra') {
    const half = parseFloat((taxTotal / 2).toFixed(2));
    return { taxType: 'cgst_sgst', cgst: half, sgst: half, igst: 0, total: parseFloat((half * 2).toFixed(2)), base: exclTax, grand };
  }
  return { taxType: 'igst', cgst: 0, sgst: 0, igst: parseFloat(taxTotal.toFixed(2)), total: parseFloat(taxTotal.toFixed(2)), base: exclTax, grand };
}

function grandTotalWithCoupon(couponResult, billingAddress) {
  const country = (billingAddress?.country_name || '').trim().toLowerCase();
  if (country && country !== 'india') {
    return parseFloat(Math.max((couponResult.original_price || 0) - (couponResult.discount_amount || 0), 0).toFixed(2));
  }
  return couponResult.discounted_price || 0;
}

// ─── Address form ────────────────────────────────────────────────────────────

const EMPTY_ADDRESS = {
  country_id: '', country_name: '',
  state_id: '',   state_name: '',
  city_id: '',    city_name: '',
  pincode: '',    address_line: '',
};

function AddressForm({ billingAddress, onChange, errors, countries, states, cities, loadingStates, loadingCities }) {
  const mkFld = (field, label) => ({
    label,
    value: billingAddress[field] || '',
    error: Boolean(errors[field]),
    helperText: errors[field] || '',
    size: 'medium',
    fullWidth: true,
    sx: inputSx,
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
        <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: PURPLE_MID, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <LocationOn sx={{ fontSize: 16, color: PURPLE }} />
        </Box>
        <Typography fontWeight={700} fontSize={15} color="#0f172a">Billing Address</Typography>
        <Box sx={{ ml: 'auto', fontSize: 11, fontWeight: 700, color: '#92400e', bgcolor: '#fef3c7', borderRadius: '6px', px: 1.25, py: 0.5, whiteSpace: 'nowrap' }}>
          Required for invoice
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={countries}
            getOptionLabel={(o) => (typeof o === 'string' ? o : o.country_name || '')}
            value={countries.find((c) => c.id === billingAddress.country_id) || null}
            onChange={(_, val) => onChange({ ...billingAddress, country_id: val?.id || '', country_name: val?.country_name || '', state_id: '', state_name: '', city_id: '', city_name: '' })}
            renderInput={(params) => (
              <TextField {...params} label="Country *" size="medium" error={Boolean(errors.country)} helperText={errors.country || ''} sx={inputSx} />
            )}
            isOptionEqualToValue={(o, v) => o.id === v?.id}
            noOptionsText={countries.length === 0 ? 'Loading…' : 'No options'}
            ListboxProps={{ style: { maxHeight: 180, fontSize: 14 } }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          {states.length > 0 ? (
            <Autocomplete
              options={states}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.state_name || '')}
              value={states.find((s) => s.id === billingAddress.state_id) || null}
              onChange={(_, val) => onChange({ ...billingAddress, state_id: val?.id || '', state_name: val?.state_name || '', city_id: '', city_name: '' })}
              loading={loadingStates}
              renderInput={(params) => (
                <TextField {...params} label="State *" size="medium" error={Boolean(errors.state)} helperText={errors.state || ''} sx={inputSx}
                  InputProps={{ ...params.InputProps, endAdornment: <>{loadingStates && <CircularProgress size={12} />}{params.InputProps.endAdornment}</> }}
                />
              )}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              ListboxProps={{ style: { maxHeight: 180, fontSize: 14 } }}
            />
          ) : (
            <TextField {...mkFld('state_name', 'State *')}
              onChange={(e) => onChange({ ...billingAddress, state_name: e.target.value, state_id: '' })}
              disabled={loadingStates}
              InputProps={loadingStates ? { endAdornment: <CircularProgress size={12} /> } : undefined}
            />
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          {cities.length > 0 ? (
            <Autocomplete
              options={cities}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.city_name || '')}
              value={cities.find((c) => c.id === billingAddress.city_id) || null}
              onChange={(_, val) => onChange({ ...billingAddress, city_id: val?.id || '', city_name: val?.city_name || '' })}
              loading={loadingCities}
              renderInput={(params) => (
                <TextField {...params} label="City *" size="medium" error={Boolean(errors.city)} helperText={errors.city || ''} sx={inputSx}
                  InputProps={{ ...params.InputProps, endAdornment: <>{loadingCities && <CircularProgress size={12} />}{params.InputProps.endAdornment}</> }}
                />
              )}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              ListboxProps={{ style: { maxHeight: 180, fontSize: 14 } }}
            />
          ) : (
            <TextField
              label="City *" value={billingAddress.city_name || ''}
              error={Boolean(errors.city)} helperText={errors.city || ''}
              size="medium" fullWidth sx={inputSx}
              onChange={(e) => onChange({ ...billingAddress, city_name: e.target.value, city_id: '' })}
              disabled={loadingCities}
            />
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField {...mkFld('pincode', 'Pincode / ZIP *')}
            onChange={(e) => onChange({ ...billingAddress, pincode: e.target.value })}
            size="medium"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField {...mkFld('address_line', 'Full Address *')}
            onChange={(e) => onChange({ ...billingAddress, address_line: e.target.value })}
            multiline minRows={2}
            size="medium"
          />
        </Grid>
      </Grid>
    </Box>
  );
}

// ─── Order summary ───────────────────────────────────────────────────────────

function SummaryRow({ label, value, green, last }) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" px={3} py={1.5}
      sx={{ borderBottom: last ? 'none' : '1px solid #f1f5f9' }}>
      <Typography sx={{ fontSize: 14, color: green ? '#16a34a' : '#475569', fontWeight: green ? 600 : 500 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: green ? '#16a34a' : '#1e293b' }}>
        {value}
      </Typography>
    </Box>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function SubscriptionCheckoutDialog({
  checkoutItem, onCheckoutItemChange,
  billingCurrency = 'INR', merchant,
  v2ActiveSub = null, v2Balance = 0,
  maxWidth = 'lg',
  onClose, onComplete,
}) {
  const { openRazorpay } = useRazorpay();
  const [loading,  setLoading]  = useState(false);
  const [creating, setCreating] = useState(false);
  const [error,         setError]         = useState('');
  const [result,        setResult]        = useState(null);
  const [paymentFailed, setPaymentFailed] = useState(null);

  const [couponCode,    setCouponCode]    = useState('');
  const [couponResult,  setCouponResult]  = useState(null);
  const [couponError,   setCouponError]   = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  const [billingAddress, setBillingAddress] = useState(EMPTY_ADDRESS);
  const [addressErrors,  setAddressErrors]  = useState({});
  const [prefillLoading, setPrefillLoading] = useState(false);

  const [countries,     setCountries]     = useState([]);
  const [states,        setStates]        = useState([]);
  const [cities,        setCities]        = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const prevItemKey = useRef(null);

  // Reset on new item
  useEffect(() => {
    if (!checkoutItem) return;
    const key = `${checkoutItem.type}-${checkoutItem.data?.id}-${checkoutItem.billingPeriod}`;
    if (key === prevItemKey.current) return;
    prevItemKey.current = key;
    setResult(null); setError(''); setPaymentFailed(null);
    setCouponCode(''); setCouponResult(null); setCouponError('');
    setAddressErrors({}); setBillingAddress(EMPTY_ADDRESS);
    setStates([]); setCities([]);
  }, [checkoutItem?.type, checkoutItem?.data?.id, checkoutItem?.billingPeriod]); // eslint-disable-line

  // Load countries once
  useEffect(() => {
    locationAPI.getCountries()
      .then((r) => setCountries(r.data?.data || []))
      .catch(() => {});
  }, []);

  // Load prefill when dialog opens
  useEffect(() => {
    if (!checkoutItem) return;
    setPrefillLoading(true);
    getCheckoutAddressPrefill()
      .then((res) => {
        const d = res.data || {};
        setBillingAddress({
          country_id: d.country_id || '', country_name: d.country_name || '',
          state_id:   d.state_id   || '', state_name:   d.state_name   || '',
          city_id:    d.city_id    || '', city_name:    d.city_name    || '',
          pincode:      d.pincode      || '',
          address_line: d.address_line || '',
        });
        if (d.country_id) {
          setLoadingStates(true);
          locationAPI.getStatesByCountry(d.country_id)
            .then((r) => {
              setStates(r.data?.data || []);
              if (d.state_id) {
                setLoadingCities(true);
                locationAPI.getCitiesByState(d.state_id)
                  .then((cr) => {
                    const cityList = cr.data?.data || [];
                    setCities(cityList);
                    if (!d.city_name && d.city_id) {
                      const found = cityList.find((c) => c.id === d.city_id || String(c.id) === String(d.city_id));
                      if (found) setBillingAddress((prev) => ({ ...prev, city_name: found.city_name }));
                    }
                  })
                  .catch(() => {}).finally(() => setLoadingCities(false));
              }
            })
            .catch(() => {}).finally(() => setLoadingStates(false));
        }
      })
      .catch(() => {}).finally(() => setPrefillLoading(false));
  }, [checkoutItem?.type, checkoutItem?.data?.id]); // eslint-disable-line

  const handleAddressChange = useCallback((newAddr) => {
    const prevCountry = billingAddress.country_id;
    const prevState   = billingAddress.state_id;
    setBillingAddress(newAddr);
    setAddressErrors((e) => {
      const next = { ...e };
      if (newAddr.country_name) delete next.country;
      if (newAddr.state_name)   delete next.state;
      if (newAddr.city_name)    delete next.city;
      if (newAddr.pincode)      delete next.pincode;
      if (newAddr.address_line) delete next.address_line;
      return next;
    });
    if (newAddr.country_id !== prevCountry) {
      setStates([]); setCities([]);
      if (newAddr.country_id) {
        setLoadingStates(true);
        locationAPI.getStatesByCountry(newAddr.country_id)
          .then((r) => setStates(r.data?.data || []))
          .catch(() => {}).finally(() => setLoadingStates(false));
      }
    } else if (newAddr.state_id !== prevState) {
      setCities([]);
      if (newAddr.state_id) {
        setLoadingCities(true);
        locationAPI.getCitiesByState(newAddr.state_id)
          .then((r) => setCities(r.data?.data || []))
          .catch(() => {}).finally(() => setLoadingCities(false));
      }
    }
  }, [billingAddress.country_id, billingAddress.state_id]);

  const handleApplyCoupon = useCallback(async () => {
    if (!couponCode.trim() || !checkoutItem) return;
    setCouponLoading(true); setCouponError(''); setCouponResult(null);
    try {
      const res = await validateMerchantCoupon({
        code: couponCode.trim().toUpperCase(),
        ...(checkoutItem.type === 'plan'
          ? { plan_version_id: checkoutItem.data.published_version?.id,
              billing_period:  checkoutItem.billingPeriod }
          : { addon_id: checkoutItem.data.id }),
        currency_code: checkoutItem.pricing?.currency_code || billingCurrency,
      });
      setCouponResult(res.data.coupon);
    } catch (err) {
      setCouponError('Invalid coupon');
    } finally { setCouponLoading(false); }
  }, [couponCode, checkoutItem, billingCurrency]);

  const validateAddress = useCallback(() => {
    const errs = {};
    if (!billingAddress.country_name) errs.country = 'Required';
    if (!billingAddress.state_name)   errs.state   = 'Required';
    if (!billingAddress.city_name)    errs.city    = 'Required';
    if (!billingAddress.pincode)      errs.pincode = 'Required';
    if (!billingAddress.address_line) errs.address_line = 'Required';
    setAddressErrors(errs);
    return Object.keys(errs).length === 0;
  }, [billingAddress]);

  const submitCheckout = useCallback(async () => {
    if (!checkoutItem) return;
    if (!validateAddress()) { setError('Please complete all billing address fields.'); return; }
    setError(''); setCreating(true); setLoading(false);

    const currency = checkoutItem.pricing?.currency_code || billingCurrency;
    const isPlan   = checkoutItem.type === 'plan';
    const addr     = billingAddress;

    const createOrderFn = isPlan ? createCheckoutOrder : createTopupOrder;
    const basePayload   = isPlan
      ? { plan_version_id: checkoutItem.data.published_version?.id, billing_period: checkoutItem.billingPeriod, currency_code: currency, billing_address: addr, ...(couponResult ? { coupon_code: couponResult.code } : {}) }
      : { addon_id: checkoutItem.data.id, currency_code: currency, billing_address: addr, ...(couponResult ? { coupon_code: couponResult.code } : {}) };
    const completeFn    = isPlan ? checkoutPlan : checkoutTopup;
    const description   = isPlan
      ? `Plan: ${checkoutItem.data.display_name || checkoutItem.data.plan_code}`
      : `Add-on: ${checkoutItem.data.display_name || checkoutItem.data.addon_code}`;

    let rzpOpened = false;
    try {
      const { data: orderData } = await createOrderFn(basePayload);

      if (orderData.skip_payment) {
        const res = await completeFn(basePayload);
        setResult(res.data); onComplete?.(); return;
      }

      if (FlutterServicesAPI.isFlutterApp()) {
        rzpOpened = true; setCreating(false); setLoading(true);
        sessionStorage.setItem('rzp_pending_checkout', JSON.stringify({ isPlan, completePayload: basePayload }));
        try {
          const rzpResult = await FlutterServicesAPI.openExternalRazorpay({
            key_id: orderData.key_id, amount: orderData.amount, currency: orderData.currency,
            autopay: orderData.autopay, rzp_order_id: orderData.rzp_order_id,
            razorpay_subscription_id: orderData.razorpay_subscription_id, description,
            prefill_name: merchant?.name || '', prefill_email: merchant?.email || '',
            prefill_contact: formatRazorpayContact(merchant),
            redirect_url: `${window.location.origin}/razorpay-redirect`,
            callback_url: 'glammify://razorpay/callback',
          });
          if (!rzpResult || rzpResult.failed) {
            sessionStorage.removeItem('rzp_pending_checkout');
            if (rzpResult?.failed) {
              setPaymentFailed({ variant: 'error', title: 'Payment Failed', message: rzpResult?.message || 'Payment could not be processed. Please try again.' });
            } else {
              setPaymentFailed({ variant: 'cancelled', title: 'Payment Cancelled', message: 'You cancelled the payment. Tap "Try Again" to complete your purchase.' });
            }
            return;
          }
          sessionStorage.removeItem('rzp_pending_checkout');
          const rzpF = orderData.autopay
            ? { razorpay_subscription_id: rzpResult.razorpay_subscription_id, razorpay_payment_id: rzpResult.razorpay_payment_id, razorpay_signature: rzpResult.razorpay_signature }
            : { razorpay_order_id: rzpResult.razorpay_order_id, razorpay_payment_id: rzpResult.razorpay_payment_id, razorpay_signature: rzpResult.razorpay_signature };
          const res = await completeFn({ ...basePayload, ...rzpF });
          setResult(res.data); onComplete?.();
        } catch (e) {
          setPaymentFailed({ variant: 'error', title: 'Payment Failed', message: e.response?.data?.message || e.response?.data?.error || 'Payment could not be processed. Please try again.' });
        } finally { setLoading(false); }
        return;
      }

      rzpOpened = true; setCreating(false); setLoading(false);
      try {
        await openRazorpay({
          key_id: orderData.key_id, amount: orderData.amount, currency: orderData.currency,
          autopay: orderData.autopay, rzp_order_id: orderData.rzp_order_id,
          rzp_subscription_id: orderData.razorpay_subscription_id, description,
          prefill_name: merchant?.name || '', prefill_email: merchant?.email || '',
          prefill_contact: formatRazorpayContact(merchant),
          onSuccess: async (rzpResponse) => {
            setLoading(true); setError('');
            try {
              const rzpF = orderData.autopay
                ? { razorpay_subscription_id: rzpResponse.razorpay_subscription_id, razorpay_payment_id: rzpResponse.razorpay_payment_id, razorpay_signature: rzpResponse.razorpay_signature }
                : { razorpay_order_id: rzpResponse.razorpay_order_id, razorpay_payment_id: rzpResponse.razorpay_payment_id, razorpay_signature: rzpResponse.razorpay_signature };
              const res = await completeFn({ ...basePayload, ...rzpF });
              setResult(res.data); onComplete?.();
            } catch (e) {
              setPaymentFailed({ variant: 'error', title: 'Verification Failed', message: e.response?.data?.message || e.response?.data?.error || 'Payment verification failed. Please contact support if an amount was deducted.' });
            } finally { setLoading(false); }
          },
          onDismiss: () => setPaymentFailed({ variant: 'cancelled', title: 'Payment Cancelled', message: 'You cancelled the payment. Click "Try Again" to complete your purchase.' }),
        });
      } catch { setError('Could not load payment gateway. Check your connection and try again.'); setLoading(false); }
    } catch (e) {
      setError('Something went wrong. Please try again or contact support.');
    } finally {
      setCreating(false);
      if (!rzpOpened) setLoading(false);
    }
  }, [checkoutItem, billingCurrency, merchant, openRazorpay, onComplete, couponResult, billingAddress, validateAddress]);

  if (!checkoutItem) return null;

  const handleClose = () => { onClose?.(); };

  // Pricing
  const { type, data, pricing: origPricing, billingPeriod: bp } = checkoutItem;
  const allPricing    = type === 'plan' ? (data.published_version?.pricing || []) : (data.pricing || []);
  const pricing       = (bp && allPricing.find((p) => (p.billing_period || p.billing_cycle) === bp)) || origPricing;
  const displayPrice  = parseFloat(pricing?.discounted_price_excl_tax || pricing?.mrp_excl_tax || pricing?.price || 0);
  const mrp           = parseFloat(pricing?.mrp_excl_tax || pricing?.price || 0);
  const gstInfo       = computeGSTFromAddress(billingAddress, pricing);
  const currSym       = (pricing?.currency_code || billingCurrency) === 'USD' ? '$' : '₹';
  const priceLoc      = (pricing?.currency_code || billingCurrency) === 'USD' ? 'en-US' : 'en-IN';
  const tokens        = type === 'plan' ? parseInt(pricing?.bundled_tokens || 0, 10) : parseInt(pricing?.tokens_grant || 0, 10);
  const fmt           = (n) => `${currSym} ${Number(n).toLocaleString(priceLoc, { minimumFractionDigits: 2 })}`;
  const now           = new Date();
  const expiryDate    = bp === 'yearly' ? new Date(now.setFullYear(now.getFullYear() + 1)) : bp === 'lifetime' ? null : new Date(new Date().setDate(new Date().getDate() + 30));
  const grandTotal    = couponResult ? grandTotalWithCoupon(couponResult, billingAddress) : gstInfo.grand;

  const dialogTitle = checkoutItem.type === 'plan' && v2ActiveSub
    ? `Upgrade to ${data.display_name}`
    : checkoutItem.type === 'plan' ? `Subscribe to ${data.display_name}`
    : `Purchase ${data.display_name}`;
  // ── Shared popup card style ─────────────────────────────────────────────────
  const popupPaperSx = { borderRadius: '24px', background: '#fff', overflow: 'visible', boxShadow: '0 24px 60px rgba(0,0,0,0.15)' };
  const popupBtnSx   = { fontWeight: 700, py: 1.6, textTransform: 'none', borderRadius: '12px', fontSize: 16, letterSpacing: '0.3px', transition: 'all 0.3s ease' };

  // ── Success screen ──────────────────────────────────────────────────────────
  if (result) {
    return (
      <Dialog open onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { ...popupPaperSx, borderRadius: '24px' } }}>
        <Box sx={{ position: 'relative', textAlign: 'center', pt: { xs: 3.5, sm: 5 }, px: { xs: 3, sm: 4.5 } }}>
          <IconButton onClick={handleClose} size="small"
            sx={{ position: 'absolute', right: 16, top: 16, color: '#64748b', bgcolor: '#f1f5f9', borderRadius: '10px', width: 32, height: 32, '&:hover': { bgcolor: '#e2e8f0' } }}>
            <Close sx={{ fontSize: 16 }} />
          </IconButton>
          <Box sx={{ width: { xs: 76, sm: 96 }, height: { xs: 76, sm: 96 }, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(99,24,198,0.1), rgba(79,70,229,0.1))', border: '2px solid rgba(99,24,198,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: { xs: 2, sm: 3 }, boxShadow: '0 8px 28px rgba(99,24,198,0.15)' }}>
            <CheckCircle sx={{ fontSize: { xs: 44, sm: 56 }, color: '#10b981' }} />
          </Box>
          <Typography fontWeight={800} fontSize={{ xs: 20, sm: 24 }} color="#0f172a" mb={1}>Payment Successful!</Typography>
          <Typography fontSize={14} color="#64748b" mb={{ xs: 2.5, sm: 3.5 }} fontWeight={500}>Your subscription is now active</Typography>
        </Box>

        <Box sx={{ px: { xs: 3, sm: 4.5 }, pb: 1 }}>
          {result.tokens_added > 0 && (
            <Box sx={{ background: `linear-gradient(135deg, ${PURPLE_SOFT}, ${PURPLE_MID})`, border: '1.5px solid #ddd6fe', borderRadius: '16px', p: { xs: 2, sm: 2.5 }, mb: 2, display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 2.5 } }}>
              <Box sx={{ width: { xs: 42, sm: 52 }, height: { xs: 42, sm: 52 }, borderRadius: '14px', background: `linear-gradient(135deg, ${PURPLE}, #4f46e5)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 14px rgba(99,24,198,0.3)' }}>
                <Stars sx={{ fontSize: { xs: 20, sm: 26 }, color: '#fff' }} />
              </Box>
              <Box>
                <Typography fontSize={11} fontWeight={800} color={PURPLE} textTransform="uppercase" letterSpacing={1.1} mb={0.5}>Tokens Added</Typography>
                <Typography fontSize={{ xs: 20, sm: 24 }} fontWeight={900} color="#0f172a" lineHeight={1.1}>+{Number(result.tokens_added).toLocaleString('en-IN')}</Typography>
              </Box>
            </Box>
          )}

          {result.carry_forward > 0 && (
            <Box sx={{ bgcolor: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: '14px', p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <AddCircle sx={{ fontSize: 22, color: '#16a34a' }} />
              </Box>
              <Box>
                <Typography fontSize={11} fontWeight={800} color="#15803d" textTransform="uppercase" letterSpacing={1.1} mb={0.5}>Carried Forward</Typography>
                <Typography fontSize={{ xs: 15, sm: 18 }} fontWeight={800} color="#15803d">+{Number(result.carry_forward).toLocaleString('en-IN')} tokens</Typography>
              </Box>
            </Box>
          )}

          {result.expiry_date && (
            <Box sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <CalendarToday sx={{ fontSize: 15, color: '#94a3b8' }} />
                <Typography fontSize={14} color="#64748b" fontWeight={500}>Active until</Typography>
              </Box>
              <Typography fontSize={14} fontWeight={700} color="#0f172a">
                {new Date(result.expiry_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ px: { xs: 3, sm: 4.5 }, pt: { xs: 2.5, sm: 3 }, pb: { xs: 3.5, sm: 4.5 } }}>
          <Button fullWidth variant="contained" onClick={handleClose}
            sx={{
              ...popupBtnSx,
              borderRadius: '12px',
              fontSize: 16,
              background: `linear-gradient(135deg, ${PURPLE}, #4f46e5)`,
              '&:hover': {
                background: `linear-gradient(135deg, ${PURPLE_DARK}, #4338ca)`,
                transform: 'translateY(-1px)',
                boxShadow: '0 8px 24px rgba(99,24,198,0.35)'
              }
            }}>
            Done
          </Button>
        </Box>
      </Dialog>
    );
  }

  // ── Failed / Cancelled screen ───────────────────────────────────────────────
  if (paymentFailed) {
    const isCancelled = paymentFailed.variant === 'cancelled';
    const accentClr   = isCancelled ? '#f59e0b' : '#ef4444';
    const accentAlpha = isCancelled ? 'rgba(245,158,11,0.06)' : 'rgba(239,68,68,0.06)';
    const accentBdr   = isCancelled ? 'rgba(245,158,11,0.18)' : 'rgba(239,68,68,0.15)';
    const accentShadow= isCancelled ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)';
    const StatusIcon  = isCancelled ? CancelOutlined : ErrorOutline;

    return (
      <Dialog open onClose={() => setPaymentFailed(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { ...popupPaperSx, borderRadius: '24px' } }}>
        <Box sx={{ position: 'relative', textAlign: 'center', pt: { xs: 3.5, sm: 5 }, px: { xs: 3, sm: 4.5 } }}>
          <IconButton onClick={() => setPaymentFailed(null)} size="small"
            sx={{ position: 'absolute', right: 16, top: 16, color: '#94a3b8', bgcolor: '#f1f5f9', borderRadius: '10px', width: 32, height: 32, '&:hover': { bgcolor: '#e2e8f0' } }}>
            <Close sx={{ fontSize: 16 }} />
          </IconButton>
          <Box sx={{ width: { xs: 76, sm: 96 }, height: { xs: 76, sm: 96 }, borderRadius: '50%', background: accentAlpha, border: `2px solid ${accentBdr}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: { xs: 2, sm: 3 }, boxShadow: `0 8px 28px ${accentShadow}` }}>
            <StatusIcon sx={{ fontSize: { xs: 44, sm: 56 }, color: accentClr }} />
          </Box>
          <Typography fontWeight={800} fontSize={{ xs: 20, sm: 24 }} color="#0f172a" mb={1}>{paymentFailed.title}</Typography>
          <Typography fontSize={14} color="#64748b" mb={{ xs: 2.5, sm: 3.5 }} sx={{ maxWidth: 280, mx: 'auto', lineHeight: 1.6, fontWeight: 500 }}>{paymentFailed.message}</Typography>
        </Box>

        <Box sx={{ px: { xs: 3, sm: 4.5 }, pb: { xs: 3.5, sm: 4.5 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Button fullWidth variant="contained" onClick={() => setPaymentFailed(null)}
            sx={{
              ...popupBtnSx,
              borderRadius: '12px',
              fontSize: 16,
              background: `linear-gradient(135deg, ${PURPLE}, #4f46e5)`,
              '&:hover': {
                background: `linear-gradient(135deg, ${PURPLE_DARK}, #4338ca)`,
                transform: 'translateY(-1px)',
                boxShadow: '0 8px 24px rgba(99,24,198,0.35)'
              }
            }}>
            Try Again
          </Button>
          <Button fullWidth variant="outlined" onClick={handleClose}
            sx={{
              ...popupBtnSx,
              borderRadius: '12px',
              fontSize: 15,
              borderColor: '#cbd5e1',
              color: '#64748b',
              '&:hover': {
                bgcolor: '#f1f5f9',
                borderColor: '#94a3b8',
                color: '#0f172a'
              }
            }}>
            Close
          </Button>
        </Box>
      </Dialog>
    );
  }

  // ── Checkout screen ────────────────────────────────────────────────────────
  return (
    <Dialog
      open onClose={handleClose} maxWidth={maxWidth} fullWidth
      PaperProps={{ sx: { borderRadius: '24px', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0, 0, 0, 0.15)', overflow: 'hidden' } }}
    >
      {/* ── Header ── */}
      <Box sx={{ px: { xs: 2.5, sm: 4 }, pt: 3, pb: 2.5, borderBottom: '1px solid #f1f5f9', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography fontWeight={800} fontSize={22} color="#0f172a" lineHeight={1.2}>{dialogTitle}</Typography>
          <Typography fontSize={13} color="#64748b" mt={0.5} fontWeight={500}>Fill billing details · secure checkout</Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={creating}
          sx={{ bgcolor: '#f1f5f9', borderRadius: '10px', width: 34, height: 34, color: '#64748b', '&:hover': { bgcolor: '#e2e8f0', color: '#0f172a' } }}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* ── Scrollable body ── */}
      <DialogContent sx={{ p: { xs: 2.5, sm: 4 }, overflowY: 'auto', flex: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: '12px', fontSize: 13 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Two-column layout */}
        <Grid container spacing={{ xs: 3, md: 4 }} alignItems="flex-start">

          {/* ── RIGHT: Order summary (on top for mobile) ── */}
          <Grid item xs={12} md={5} order={{ xs: 1, md: 2 }}>
            <Box sx={{
              bgcolor: '#ffffff',
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.04)',
              overflow: 'hidden'
            }}>

              {/* Plan chip */}
              <Box sx={{
                background: 'linear-gradient(135deg, rgba(99, 24, 198, 0.03) 0%, rgba(99, 24, 198, 0.07) 100%)',
                borderBottom: '1px solid #f1f5f9',
                px: 3,
                py: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <Box sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '12px',
                  bgcolor: PURPLE_MID,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  border: '1px solid rgba(99, 24, 198, 0.1)'
                }}>
                  <Payment sx={{ fontSize: 20, color: PURPLE }} />
                </Box>
                <Box>
                  <Typography fontWeight={800} fontSize={16} color="#0f172a">{data.display_name}</Typography>
                  <Typography fontSize={12} color="#64748b" fontWeight={500} mt={0.25}>
                    {tokens > 0 ? `${Number(tokens).toLocaleString('en-IN')} Tokens` : 'Feature Access'}
                    {bp ? ` · ${bp.replace('_', ' ')}` : type === 'addon' ? ' · One-time' : ''}
                  </Typography>
                </Box>
              </Box>

              {/* Billing period tabs */}
              {type === 'plan' && allPricing.length > 1 && onCheckoutItemChange && (
                <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
                  <Box sx={{ display: 'flex', bgcolor: '#f1f5f9', borderRadius: '12px', p: '4px', gap: '4px' }}>
                    {allPricing.map((p) => {
                      const period = p.billing_period || p.billing_cycle;
                      const label  = { monthly: 'Monthly', yearly: 'Yearly', one_time: 'One-time' }[period] || period;
                      const isSelected = bp === period;
                      return (
                        <Box key={period} onClick={() => onCheckoutItemChange((ci) => ({ ...ci, billingPeriod: period, pricing: p }))}
                          sx={{
                            flex: 1,
                            py: 1,
                            borderRadius: '10px',
                            textAlign: 'center',
                            fontSize: 13,
                            fontWeight: 700,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            ...(isSelected
                              ? { bgcolor: '#fff', color: PURPLE, boxShadow: '0 2px 8px rgba(99, 24, 198, 0.08)' }
                              : { color: '#64748b', '&:hover': { color: '#0f172a' } }
                            )
                          }}>
                          {label}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Price rows */}
              <Box sx={{ borderTop: '1px solid #f1f5f9', pt: 1, pb: 1 }}>
                <SummaryRow label={type === 'addon' ? 'Pack price' : 'Package Price'} value={fmt(displayPrice)} />
                {displayPrice < mrp && <SummaryRow label="Discount" value={`− ${fmt(mrp - displayPrice)}`} green />}
                {couponResult && <SummaryRow label={`Coupon (${couponResult.code})`} value={`− ${fmt(couponResult.discount_amount || 0)}`} green />}
                {gstInfo.taxType === 'cgst_sgst' && <>
                  <SummaryRow label="CGST (9%)" value={fmt(gstInfo.cgst)} />
                  <SummaryRow label="SGST (9%)" value={fmt(gstInfo.sgst)} />
                </>}
                {gstInfo.taxType === 'igst' && <SummaryRow label="IGST (18%)" value={fmt(gstInfo.igst)} />}
                {gstInfo.taxType === 'none' && <SummaryRow label="Tax" value="₹ 0.00 (Non-India)" />}
              </Box>

              {/* Total — gradient */}
              <Box sx={{
                background: `linear-gradient(135deg, ${PURPLE} 0%, #4f46e5 100%)`,
                px: 3,
                py: 2.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography fontWeight={700} fontSize={14} color="rgba(255, 255, 255, 0.9)">Total Amount</Typography>
                <Box sx={{ textAlign: 'right' }}>
                  {couponResult && (
                    <Typography sx={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.6)', textDecoration: 'line-through', lineHeight: 1, mb: 0.5 }}>
                      {fmt(gstInfo.grand)}
                    </Typography>
                  )}
                  <Typography fontWeight={800} fontSize={22} color="#fff" lineHeight={1}>{fmt(grandTotal)}</Typography>
                </Box>
              </Box>

              {/* Footer notes */}
              {(bp === 'monthly' || bp === 'yearly') && pricing?.is_auto_renew && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 3, py: 2 }}>
                  <Autorenew sx={{ fontSize: 14, color: PURPLE, mt: '2px', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12, color: '#4f46e5', lineHeight: 1.5, fontWeight: 500 }}>
                    Auto-renews every <strong>{bp === 'yearly' ? 'year' : 'month'}</strong>
                    {expiryDate && <> · Next <strong>{expiryDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</strong></>}
                    {' '}· Cancel anytime
                  </Typography>
                </Box>
              )}
              {gstInfo.taxType !== 'none' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 1.5, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                  <Shield sx={{ fontSize: 13, color: '#64748b', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>
                    {gstInfo.taxType === 'cgst_sgst' ? 'CGST + SGST · Maharashtra' : 'IGST · India'} · Invoice updates live
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Footer notes under summary card */}
            {expiryDate && type === 'plan' && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, mt: 1.5 }}>
                <Typography fontSize={12} color="text.secondary">Plan valid until</Typography>
                <Typography fontSize={12} fontWeight={700} color="#0f172a">{expiryDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</Typography>
              </Box>
            )}
            {type === 'addon' && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, px: 1, fontSize: 12 }}>Tokens expire with your active subscription.</Typography>}
          </Grid>

          {/* ── LEFT: Address + Coupon ── */}
          <Grid item xs={12} md={7} order={{ xs: 2, md: 1 }}>
            {/* Address container */}
            <Box sx={{
              bgcolor: '#ffffff',
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              p: { xs: 2.5, sm: 3.5 },
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.02)',
              mb: 3
            }}>
              {prefillLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                  <CircularProgress size={16} sx={{ color: PURPLE }} />
                  <Typography fontSize={14} color="text.secondary">Loading your address…</Typography>
                </Box>
              ) : (
                <AddressForm
                  billingAddress={billingAddress} onChange={handleAddressChange}
                  errors={addressErrors} countries={countries}
                  states={states} cities={cities}
                  loadingStates={loadingStates} loadingCities={loadingCities}
                />
              )}
            </Box>

            {/* Coupon */}
            <Box sx={{
                bgcolor: '#ffffff',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                p: { xs: 2.5, sm: 3 },
                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.02)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <LocalOffer sx={{ fontSize: 15, color: '#64748b' }} />
                  <Typography fontSize={14} fontWeight={600} color="#0f172a">Have a coupon code?</Typography>
                </Box>
                {!couponResult ? (
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <TextField
                      size="medium" placeholder="ENTER CODE"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                      sx={{ flex: 1, ...inputSx, '& input': { letterSpacing: 2, fontWeight: 700, fontSize: 13 } }}
                    />
                    <Button variant="outlined" onClick={handleApplyCoupon}
                      disabled={couponLoading || !couponCode.trim()}
                      sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        borderColor: PURPLE,
                        color: PURPLE,
                        px: 3,
                        fontSize: 14,
                        '&:hover': {
                          borderColor: PURPLE_DARK,
                          bgcolor: 'rgba(99, 24, 198, 0.04)',
                        }
                      }}>
                      {couponLoading ? <CircularProgress size={14} sx={{ color: PURPLE }} /> : 'Apply'}
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', px: 2, py: 1.25 }}>
                    <Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#15803d' }}>✓ {couponResult.code} applied</Typography>
                      {couponResult.description && <Typography sx={{ fontSize: 12, color: '#166534', mt: 0.25 }}>{couponResult.description}</Typography>}
                    </Box>
                    <IconButton size="small" onClick={() => { setCouponCode(''); setCouponResult(null); setCouponError(''); }} sx={{ color: '#64748b' }}>
                      <Close sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                )}
                {couponError && <Typography sx={{ fontSize: 12, color: 'error.main', mt: 0.75 }}>{couponError}</Typography>}
              </Box>
          </Grid>
        </Grid>
      </DialogContent>

      {/* ── Footer Actions ── */}
      <Box sx={{ px: { xs: 2.5, sm: 4 }, pb: 3, pt: 2, borderBottom: '1px solid #f1f5f9', borderTop: '1px solid #f1f5f9', flexShrink: 0, bgcolor: '#fff', display: 'flex', flexDirection: 'column', gap: 1 }}>
        {checkoutItem.type === 'plan' && v2ActiveSub && v2Balance > 0 && (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', p: 1.5, mb: 0.5 }}>
            <Autorenew sx={{ fontSize: 16, color: '#16a34a', mt: 0.15, flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#15803d' }}>
                {Number(v2Balance).toLocaleString('en-IN')} unused tokens will carry forward
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: '#166534', mt: 0.25 }}>
                Valid until new plan expires + 90 days
              </Typography>
            </Box>
          </Box>
        )}
        <Button fullWidth variant="contained" onClick={submitCheckout} disabled={loading || creating}
          startIcon={(loading || creating) ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{
            background: (loading || creating) ? '#cbd5e1' : `linear-gradient(135deg, ${PURPLE} 0%, #4f46e5 100%)`,
            borderRadius: '12px',
            fontWeight: 700,
            textTransform: 'none',
            fontSize: 16,
            py: 1.6,
            boxShadow: '0 4px 14px rgba(99, 24, 198, 0.25)',
            transition: 'all 0.2s ease',
            '&:hover:not(:disabled)': {
              background: `linear-gradient(135deg, ${PURPLE_DARK} 0%, #4338ca 100%)`,
              boxShadow: '0 6px 20px rgba(99, 24, 198, 0.35)',
              transform: 'translateY(-1px)',
            },
            '&:active:not(:disabled)': { transform: 'translateY(0)' },
          }}>
          {creating ? 'Preparing order…' : loading ? 'Processing…' : `Pay Now · ${fmt(grandTotal)}`}
        </Button>
        <Button fullWidth onClick={handleClose} disabled={creating}
          sx={{ textTransform: 'none', fontWeight: 600, color: '#9ca3af', fontSize: 13, '&:hover': { bgcolor: 'transparent', color: '#374151' } }}>
          Cancel
        </Button>
      </Box>
    </Dialog>
  );
}
